import { consola } from 'consola'
import { ref, readonly } from 'vue'
import { 
  openOrderDetailsModal,
  handleOrderDetailsLoadError,
  handleRetryPaymentError, 
} from '../lib/modalUtils'
import { 
  initiatePaymentBySystem,
  validateOrderForPayment,
  logPaymentSystemInfo, 
} from '../lib/paymentSystemUtils'
import type { Order } from '@entities/order'

/**
 * Composable для действий с платежами
 */
export function usePaymentActions() {
  const isLoading = ref(false)
  const error = ref<string | null>(null)

  /**
   * Открывает модальное окно с деталями заказа
   */
  async function openOrderDetailsModalAction(
    order: Order | null,
    isLoadingDetails: boolean,
    loadOrderDetails: (orderId: string | number) => Promise<Order | null>,
  ): Promise<void> {
    if (!order?.id) {
      consola.warn('No order ID available')
      return
    }
    
    if (isLoadingDetails) {
      consola.debug('Already loading details, skipping modal open')
      return
    }
    
    consola.debug('Opening order details modal for order:', order.id)
    
    // Всегда загружаем полную информацию для актуальности данных
    const fullOrderData = await loadOrderDetails(order.id)
    
    if (fullOrderData) {
      consola.debug('Opening ModalThankYou with full order data')
      openOrderDetailsModal(fullOrderData)
    } else {
      handleOrderDetailsLoadError()
    }
  }

  /**
   * Инициирует повторную оплату заказа
   */
  function initiateRetryPayment(
    order: Order | null,
    setAmount: (amount: number) => void,
    setOrderId: (orderId: number) => void,
    setConfirmationUrl: (url: string) => void,
  ): void {
    if (!order || isLoading.value) {
      consola.debug('Cannot initiate retry payment: no order or loading')
      return
    }
    
    if (!validateOrderForPayment(order)) {
      consola.warn('Order validation failed for retry payment')
      return
    }
    
    isLoading.value = true
    error.value = null
    
    try {
      consola.debug('Initiating retry payment for order:', order.id)
      logPaymentSystemInfo(order)
      
      // Устанавливаем данные для оплаты в store
      if (order.payment_url) {
        setConfirmationUrl(order.payment_url)
      }
      if (order.total_amount) {
        setAmount(order.total_amount)
      }
      if (order.id) {
        setOrderId(Number(order.id))
      }
      
      // Инициируем оплату через соответствующую систему
      const paymentInitiated = initiatePaymentBySystem(order)
      
      if (!paymentInitiated) {
        throw new Error('Нет доступного способа оплаты для этого заказа')
      }
      
    } catch (error_: any) {
      consola.error('Retry payment failed:', error_)
      error.value = error_.message || 'Ошибка при попытке оплаты'
      handleRetryPaymentError()
    } finally {
      isLoading.value = false
    }
  }

  return {
    // State
    isLoading: readonly(isLoading),
    error: readonly(error),
    
    // Actions
    openOrderDetailsModalAction,
    initiateRetryPayment,
  }
}